import { JSX, FC, useEffect, useState, SetStateAction, Dispatch } from "react";
import dayjs from "dayjs";

// types
import { ProductsFiltersValuesType } from "app/types";
import { OptionType } from "utils/formatCategories";

// redux
import { useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";

// components
import FiltersCheckbox from "../FiltersCheckbox";
import FilterWrapper from "components/ui/Filters/FiltersDrawer/FilterWrapper";
import ProductsCategoryFilters from "./ProductsCategoryFilters";

// utils
import { formatPrice } from "utils/formatPrice";
import { getPriceStep } from "./utils/getPriceStep";

// antd
import { RangePickerProps } from "antd/es/date-picker";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import {
  Form,
  Slider,
  Collapse,
  DatePicker,
  InputNumber,
  FormInstance,
  CollapseProps,
} from "antd";

interface IProductsFiltersProps {
  form: FormInstance<ProductsFiltersValuesType>;
  values: ProductsFiltersValuesType;
  params: URLSearchParams;
  categoriesTree: OptionType[];
  categoriesTPTree: OptionType[];
  setSelectedCategories: Dispatch<SetStateAction<number[]>>;
  setSelectedCategoriesTP: Dispatch<SetStateAction<number[]>>;
}

const productStatuses: { key: string, label: string, name: string }[] = [
  { key: "1", label: "Размещен", name: "published", },
  // { key: "2", label: "На модерации", value: "moderation", }, TODO на данный момент не используется
  // { key: "3", label: "Отклонен", value: "rejected", }, TODO на данный момент не используется
  // { key: "4", label: "Требует обновления", value: "needs_update", }, TODO на данный момент не используется
  { key: "5", label: "Ошибка", name: "error", },
  { key: "6", label: "Черновик", name: "draft", },
];

const ProductsFilters: FC<IProductsFiltersProps> = ({
  form,
  values,
  categoriesTree,
  categoriesTPTree,
  setSelectedCategories,
  setSelectedCategoriesTP,
}): JSX.Element => {
  const { minPrice, maxPrice } = useSelector((state: AppStateType) => state.productsSlice.products);

  const [selectForm, setSelectForm] = useState<string>("");

  useEffect(() => {
    if (values?.price?.some((el: number): boolean => el === null)) {
      form.setFieldsValue({ price: [minPrice, maxPrice] });
    }

    if (values?.maxPrice && selectForm === "maxPrice") {
      form.setFieldsValue({ price: [values?.minPrice || minPrice, values?.maxPrice] });
    }

    if (values?.minPrice && selectForm === "minPrice") {
      form.setFieldsValue({ price: [values?.minPrice, values?.maxPrice || maxPrice] });
    }
  }, [values?.minPrice, values?.maxPrice, minPrice, maxPrice]);

  useEffect(() => {
    if (values?.price?.length && selectForm === "price") {
      form.setFieldsValue({
        minPrice: values?.price[0],
        maxPrice: values?.price[1],
      });
    }
  }, [values?.price]);

  const renderStatuses = (): JSX.Element[] => productStatuses.map(({ key, label, name }) => {
    const handleChange = (e: CheckboxChangeEvent): void => form.setFieldsValue({ [name]: e.target.checked });

    return (
      <FiltersCheckbox
        key={key}
        label={label}
        name={name}
        checked={values?.[name]}
        handleChange={handleChange}
      />
    );
  });

  const collapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: "По статусу",
      children: renderStatuses(),
      className: "p-0 font-bold",
    },
  ];

  const disabledDate: RangePickerProps["disabledDate"] = (current: dayjs.Dayjs): boolean => current > dayjs();

  const handleInputChange = (fieldName: "minPrice" | "maxPrice") => (value: number): void => {
    if (value !== null) {
      form.setFieldsValue({ [fieldName]: value });
      setSelectForm(fieldName);
    }
  };

  const handleBlur = (fieldName: "minPrice" | "maxPrice") => (): void => {
    const { minPrice: currentMin, maxPrice: currentMax } = form.getFieldsValue(["minPrice", "maxPrice"]);

    if (currentMin == null || currentMax == null) return;

    const step: number = getPriceStep(minPrice);
    let newMin: number = currentMin;
    let newMax: number = currentMax;

    if (fieldName === "minPrice" && currentMin > currentMax) {
      newMax = Math.min(currentMin + step, maxPrice);
    }

    if (fieldName === "maxPrice" && currentMax < currentMin) {
      newMin = Math.max(currentMax - step, minPrice);
    }

    form.setFieldsValue({
      minPrice: newMin,
      maxPrice: newMax,
      price: [newMin, newMax],
    });
  };

  const handleSelectSlider = (): void => setSelectForm("price");

  return (
    <div>
      <Collapse
        ghost
        defaultActiveKey={["1"]}
        items={collapseItems}
        bordered={false}
        expandIconPosition="end"
        className="mb-5"
      />
      <FilterWrapper
        label="По цене"
        value={
          <span className="secondary-color">
            {formatPrice(values?.price?.[0] ?? minPrice ?? 0)} ₽ - {formatPrice(values?.price?.[1] ?? maxPrice ?? 0)} ₽
          </span>
        }
      >
        <Form.Item className="m-0" name="price" initialValue={[minPrice, maxPrice]}>
          <Slider
            range
            step={1}
            min={minPrice}
            max={maxPrice}
            styles={{
              track: { background: "#4786FF" }
            }}
            onChange={handleSelectSlider}
          />
        </Form.Item>
        <div className="flex gap-[102px]">
          <Form.Item className="m-0" name="minPrice" initialValue={minPrice}>
            <InputNumber
              size="large"
              type="number"
              style={{ width: "100%" }}
              min={minPrice}
              max={maxPrice}
              step={1}
              onChange={handleInputChange("minPrice")}
              onBlur={handleBlur("minPrice")}
            />
          </Form.Item>
          <Form.Item className="m-0" name="maxPrice" initialValue={maxPrice}>
            <InputNumber
              size="large"
              type="number"
              style={{ width: "100%" }}
              min={minPrice}
              max={maxPrice}
              step={1}
              onChange={handleInputChange("maxPrice")}
              onBlur={handleBlur("maxPrice")}
            />
          </Form.Item>
        </div>
      </FilterWrapper>
      <ProductsCategoryFilters
        values={values}
        categoriesTree={categoriesTree}
        categoriesTPTree={categoriesTPTree}
        setSelectedCategories={setSelectedCategories}
        setSelectedCategoriesTP={setSelectedCategoriesTP}
      />
      <FilterWrapper label="По дате обновления товара">
        <Form.Item className="m-0" name="date" initialValue={null}>
          <DatePicker.RangePicker
            className="w-full"
            size="large"
            placeholder={["С даты", "По дату"]}
            defaultPickerValue={[dayjs().add(-1, "M"), dayjs()]}
            disabledDate={disabledDate}
            format="DD.MM.YYYY"
          />
        </Form.Item>
      </FilterWrapper>
    </div>
  );
};

export default ProductsFilters;
